import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, Copy, Check, Bot, ExternalLink, ShieldCheck, KeyRound, ChevronDown } from 'lucide-react';
import { apiKeysApi } from '../../../api/endpoints/apiKeys';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../components/ui/Alert';
import claudeLogo from '../../../assets/logos/claude.png';
import chatgptLogo from '../../../assets/logos/chatgpt.png';

interface Props { storeId: string; }

// Single remote MCP endpoint that works for both Claude and ChatGPT. With OAuth
// the assistant discovers sign in automatically, so the URL carries no store or
// key. Derived from the API base so it matches the environment (localhost in
// dev, 00pays.com in production). Cloud assistants can only reach the public URL.
const API_BASE = import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api/v1`;
const MCP_URL = `${API_BASE.replace(/\/$/, '')}/mcp`;

const CLAUDE_LOGO = claudeLogo;
const CHATGPT_LOGO = chatgptLogo;

const SCOPE_PRESETS: Record<string, { label: string; permissions: string[] }> = {
  readonly: { label: 'Read only (recommended)', permissions: ['products.read', 'orders.read', 'customers.read', 'analytics.read', 'reviews.read', 'settings.read', 'pages.read', 'design.read'] },
  readwrite: { label: 'Read and write', permissions: ['products.*', 'orders.*', 'customers.*', 'coupons.*', 'reviews.write', 'shipping.*', 'pages.*', 'design.*'] },
  full: { label: 'Full access', permissions: ['*'] },
};

export function AiConnectorsTab({ storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [copied, setCopied] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [preset, setPreset] = useState<keyof typeof SCOPE_PRESETS>('readonly');
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const copy = async (value: string, id: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(id);
      toast('success', t('settings.copiedToClipboard', 'Copied to clipboard'));
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
    } catch {
      toast('error', t('settings.copyFailed', 'Failed to copy'));
    }
  };

  const keyMutation = useMutation({
    mutationFn: () =>
      apiKeysApi.create(storeId, {
        name: `MCP API key: ${SCOPE_PRESETS[preset].label}`,
        permissions: SCOPE_PRESETS[preset].permissions,
      }),
    onSuccess: (result) => {
      setCreatedKey(result.apiKey);
      setKeyModalOpen(true);
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Intro */}
      <section className="rounded-xl border border-accent/25 bg-accent/10 p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-accent/20 p-2.5 shrink-0">
            <Sparkles className="h-5 w-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-text-primary">
              {t('settingsAiConnectors.title', 'Connect AI assistants')}
            </h2>
            <p className="text-sm text-text-secondary mt-1 leading-relaxed">
              {t(
                'settingsAiConnectors.intro',
                'Let Claude or ChatGPT manage your store through a secure connection (MCP). Add the URL below in your assistant, then sign in to Bemo and approve the access level. No key copying needed.',
              )}
            </p>
          </div>
        </div>
      </section>

      {/* MCP URL */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-text-muted" />
          <h2 className="text-base font-semibold">{t('settingsAiConnectors.serverUrl', 'MCP server URL')}</h2>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-bg-field px-3 py-2">
          <p className="font-mono text-sm text-text-primary truncate">{MCP_URL}</p>
          <Button variant="ghost" size="sm" className="shrink-0" onClick={() => copy(MCP_URL, 'url')}>
            {copied === 'url' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            {copied === 'url' ? t('settings.copied', 'Copied') : t('settings.copy', 'Copy')}
          </Button>
        </div>
      </section>

      {/* Per-provider instructions */}
      <section className="grid gap-4 sm:grid-cols-2">
        <ProviderCard
          name="Claude"
          logo={CLAUDE_LOGO}
          steps={CLAUDE_STEPS}
          link="https://claude.ai/customize/connectors?modal=add-custom-connector"
          linkLabel={t('settingsAiConnectors.openClaude', 'Open Claude connectors')}
          t={t}
          idPrefix="claude"
        />
        <ProviderCard
          name="ChatGPT"
          logo={CHATGPT_LOGO}
          steps={CHATGPT_STEPS}
          link="https://chatgpt.com/#settings/Connectors"
          linkLabel={t('settingsAiConnectors.openChatgpt', 'Open ChatGPT connectors')}
          t={t}
          idPrefix="chatgpt"
        />
      </section>

      {/* Advanced: API key fallback */}
      <section className="rounded-xl border border-border-default bg-bg-secondary">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex w-full items-center justify-between gap-2 p-5 text-start"
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <KeyRound className="h-4 w-4 text-text-muted" />
            {t('settingsAiConnectors.advanced', 'Advanced: connect with an API key instead')}
          </span>
          <ChevronDown className={`h-4 w-4 text-text-muted transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>
        {showAdvanced && (
          <div className="space-y-4 border-t border-border-default p-5">
            <p className="text-sm text-text-secondary leading-relaxed">
              {t(
                'settingsAiConnectors.advancedDesc',
                'If your assistant does not support OAuth sign in, generate a key and paste it as a Bearer token. Choose the access level it should have:',
              )}
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {(Object.keys(SCOPE_PRESETS) as (keyof typeof SCOPE_PRESETS)[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPreset(key)}
                  className={`rounded-lg border px-3 py-3 text-sm font-medium text-start transition-colors ${
                    preset === key
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border-default text-text-secondary hover:border-accent/50'
                  }`}
                >
                  {t(`settingsAiConnectors.preset_${key}`, SCOPE_PRESETS[key].label)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('settingsAiConnectors.revokeHint', 'You can revoke any key anytime from the API keys tab.')}
            </div>
            <Button onClick={() => keyMutation.mutate()} isLoading={keyMutation.isPending}>
              {t('settingsAiConnectors.generateKey', 'Generate API key')}
            </Button>
          </div>
        )}
      </section>

      <Modal
        isOpen={keyModalOpen}
        onClose={() => { setKeyModalOpen(false); setCreatedKey(null); }}
        title={t('settingsAiConnectors.apiKeyCreated', 'API key created')}
        size="lg"
        footer={<Button onClick={() => { setKeyModalOpen(false); setCreatedKey(null); }}>{t('common.close')}</Button>}
      >
        {createdKey && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              {t('settingsAiConnectors.saveKeyWarning', 'Copy these values now. The key is shown only once, so keep it secret.')}
            </p>
            <Field label={t('settingsAiConnectors.serverUrl', 'MCP server URL')} value={MCP_URL} copied={copied === 'murl'} onCopy={() => copy(MCP_URL, 'murl')} />
            <Field label={t('settingsAiConnectors.apiKey', 'API key (use as Bearer token)')} value={createdKey} copied={copied === 'mkey'} onCopy={() => copy(createdKey, 'mkey')} mono />
          </div>
        )}
      </Modal>
    </div>
  );
}

const CLAUDE_STEPS = [
  'Open Settings, then Connectors, then Add custom connector.',
  'Paste the MCP server URL above and save.',
  'Click Connect, sign in to Bemo, and approve the store and access level.',
  'Enable the connector in a new chat.',
];

const CHATGPT_STEPS = [
  'Enable Developer mode under Settings, then Connectors (or Apps).',
  'Add a connector and paste the MCP server URL above.',
  'Authorize, sign in to Bemo, and approve the store and access level.',
  'Turn the connector on for your chat or deep research.',
];

function ProviderCard({ name, logo, steps, link, linkLabel, t, idPrefix }: {
  name: string; logo: string; steps: string[]; link: string; linkLabel: string;
  t: ReturnType<typeof useTranslation>['t']; idPrefix: string;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  return (
    <div className="flex flex-col rounded-xl border border-border-default bg-bg-secondary p-5">
      <div className="flex items-center gap-2.5 mb-2">
        {logoFailed ? (
          <Bot className="h-6 w-6 text-accent" />
        ) : (
          <img
            src={logo}
            alt={name}
            className="h-6 w-6 rounded object-contain"
            onError={() => setLogoFailed(true)}
          />
        )}
        <h3 className="text-base font-semibold">{name}</h3>
      </div>
      <ol className="list-decimal space-y-1.5 ps-5 text-sm text-text-secondary leading-relaxed flex-1">
        {steps.map((step, i) => (
          <li key={i}>{t(`settingsAiConnectors.${idPrefix}_step_${i}`, step)}</li>
        ))}
      </ol>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
      >
        {linkLabel}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

function Field({ label, value, copied, onCopy, mono }: { label: string; value: string; copied: boolean; onCopy: () => void; mono?: boolean }) {
  return (
    <div>
      <label className="block text-xs text-text-muted mb-1">{label}</label>
      <div className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-field px-3 py-2">
        <code className={`flex-1 break-all text-sm text-text-primary ${mono ? 'font-mono' : ''}`}>{value}</code>
        <button onClick={onCopy} className="shrink-0 rounded-lg p-1.5 text-text-muted hover:text-accent" type="button">
          {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
